import type { AppContext } from '../../app';

/**
 * ALDS surface registry.
 *
 * Four role-shaped persistent surfaces (`env-intake`, `env-board`,
 * `env-roster`, `env-audit`) on top of the existing Surface Mesh primitives
 * (`env-chat`, `env-vault`, `env-voice`). Plus a `hub` for role selection
 * and a set of transient surfaces for the dispatch handshake.
 *
 * Surface IDs are independent of `Context.environment`, which is what gates
 * signing-context derivation. We reuse the existing environment union
 * (`hub` | `funnel` | `chat` | `voice` | `vault`) rather than extending it,
 * which would require touching the identity layer.
 *
 * Exchange-environment surfaces (Surface Mesh's crypto exchange) are
 * deliberately omitted for ALDS — out of scope for mutual aid.
 *
 * See `docs/alds-surface-map.md` for ASCII mockups and role flow,
 * `docs/alds-roadmap.md` for the wiring sequence.
 */

export function registerBuiltInSurfaces(_ctx: AppContext): void {
  const { surfaces } = _ctx;

  // ── Hub: post-unlock landing, role selection ────────────────
  surfaces.register({
    id: 'hub',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'longterm',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({ role: null as null | 'caller' | 'dispatcher' | 'responder' | 'supervisor' }),
  });

  // ── Role-shaped top-level surfaces ──────────────────────────

  // Caller intake — drives the incident-intake-v1 funnel.
  surfaces.register({
    id: 'env-intake',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'session',
    environment: 'funnel',
    stateSchema: {},
    initialState: () => ({ draftId: null as string | null }),
  });

  // Dispatcher incident board — sortable list, severity badge, geo proximity.
  surfaces.register({
    id: 'env-board',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'longterm',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({
      filter: 'open' as 'open' | 'all' | 'mine',
      sortBy: 'severity' as 'severity' | 'age' | 'proximity',
    }),
  });

  // Responder roster — my assignments + pending dispatch interrupts.
  surfaces.register({
    id: 'env-roster',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'longterm',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({ onShift: false }),
  });

  // Supervisor / public audit — read-only governance view.
  surfaces.register({
    id: 'env-audit',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'longterm',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({ since: null as string | null }),
  });

  // ── Backing primitives (unchanged from Surface Mesh) ─────────

  // Incident coordination channel (one per incident).
  surfaces.register({
    id: 'env-chat',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'longterm',
    environment: 'chat',
    stateSchema: {},
    initialState: () => ({ threadId: null as string | null }),
  });

  // Evidence vault per incident (photos in v1, broader media v2).
  surfaces.register({
    id: 'env-vault',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'longterm',
    environment: 'vault',
    stateSchema: {},
    initialState: () => ({ files: [] }),
  });

  // Voice / video — paper design only in v0. Surface reserved.
  surfaces.register({
    id: 'env-voice',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'session',
    environment: 'voice',
    stateSchema: {},
    initialState: () => ({ peers: [] }),
  });

  // ── Transient surfaces (push/pop, capture-and-close) ────────

  // Caller flow.
  surfaces.register({
    id: 'incident-report',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'funnel',
    stateSchema: {},
    initialState: () => ({
      category: null as null | string,
      proposedSeverity: null as null | string,
      narrative: '',
      location: null as null | { lat: number; lng: number; accuracyM: number; fixAt: string },
      attachedPhotos: [] as string[], // manifestIds
    }),
  });

  // Attach a photo to an incident (caller- or responder-initiated).
  // v1: photos only. Audio / video deferred to v2.
  surfaces.register({
    id: 'attach-evidence',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'vault',
    stateSchema: {},
    initialState: () => ({ incidentId: '', mediaKind: 'photo' as const }),
  });

  // Dispatcher flow.
  surfaces.register({
    id: 'triage',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({
      incidentId: '',
      decision: null as null | 'assign' | 'hold' | 'escalate' | 'cancel',
      confirmedSeverity: null as null | string,
    }),
  });

  surfaces.register({
    id: 'assign-responder',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({ incidentId: '', candidatePubkey: null as null | string }),
  });

  // Responder flow.
  surfaces.register({
    id: 'responder-accept',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({ incidentId: '', decision: null as null | 'accept' | 'decline' }),
  });

  surfaces.register({
    id: 'responder-status',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'chat',
    stateSchema: {},
    initialState: () => ({
      incidentId: '',
      status: 'en-route' as 'en-route' | 'on-scene' | 'resolved' | 'unable',
    }),
  });

  surfaces.register({
    id: 'request-backup',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'chat',
    stateSchema: {},
    initialState: () => ({ incidentId: '', reason: '' }),
  });

  // Supervisor / export flow.
  surfaces.register({
    id: 'share-incident-out',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({
      incidentId: '',
      redactLevel: 'public' as 'public' | 'internal',
    }),
  });

  // Mesh peer invite — generate / redeem (Phase 6 field hardening).
  surfaces.register({
    id: 'mesh-invite-generate',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({ code: null as string | null }),
  });
  surfaces.register({
    id: 'mesh-invite-redeem',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({ code: '' }),
  });

  // Sneakernet bundle export/import (RecoveryManager bundle, no real network).
  surfaces.register({
    id: 'sneakernet',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({ mode: 'export' as 'export' | 'import' }),
  });

  // QR handoff (Phase 10) — camera-to-camera transfer of sneakernet bundles.
  surfaces.register({
    id: 'qr-handoff',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({ mode: 'show' as 'show' | 'scan' }),
  });

  // Direct message compose inside an incident thread.
  surfaces.register({
    id: 'compose-message',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'chat',
    stateSchema: {},
    initialState: () => ({ threadId: '', body: '' }),
  });

  // Voice call setup (reserved for v2 voice/video work).
  surfaces.register({
    id: 'establish-call',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'voice',
    stateSchema: {},
    initialState: () => ({ peerId: '' }),
  });

  // ── Phase 8 call surfaces (v2 voice/video) ──────────────────
  surfaces.register({
    id: 'call-incoming',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'voice',
    stateSchema: {},
    initialState: () => ({ callId: '', kind: 'audio' as 'audio' | 'video' }),
  });
  surfaces.register({
    id: 'call-active',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'session',
    environment: 'voice',
    stateSchema: {},
    initialState: () => ({ callId: '', startedAt: null as string | null }),
  });
  surfaces.register({
    id: 'call-history',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'voice',
    stateSchema: {},
    initialState: () => ({ threadId: '' }),
  });

  // Ambient — minimal floating bar visible whenever a call is active; lets
  // the user mute/end without leaving whatever surface they're on. Wired
  // by the renderer when the current call-active surface is stacked.
  surfaces.register({
    id: 'media-controls',
    version: '0.1.0',
    surfaceClass: 'ambient',
    persistence: 'session',
    environment: 'voice',
    stateSchema: {},
    initialState: () => ({
      callId: '' as string,
      audioMuted: false,
      videoOn: false,
    }),
  });

  // ── Phase 14 — multi-hop federation treaty builder ──────────────
  surfaces.register({
    id: 'treaty-builder',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({
      remoteNeighborhoodId: '',
      hopTtl: 2,
      autoForward: false,
      rules: [] as Array<{ field: string; action: 'drop' | 'coarsenGeo' | 'hash' | 'truncate'; maxLen?: number }>,
    }),
  });

  // ── Phase 19.3 — operator-paste network config (bootstrap + TURN) ───
  surfaces.register({
    id: 'network-config',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({
      bootstrapPeers: '' as string,
      turnUrl: '' as string,
      turnSecret: '' as string,
      turnRealm: '' as string,
      defaultSensitivity: 'low' as 'low' | 'medium' | 'high',
    }),
  });

  // ── Phase 18 — secure-enclave key storage config ─────────────────
  surfaces.register({
    id: 'key-storage-config',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({ label: '' }),
  });

  // ── Phase 17 — jurisdiction plugin picker ───────────────────────
  surfaces.register({
    id: 'plugin-picker',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({
      customManifestJson: '',
    }),
  });

  // ── Phase 49 (v9) — cross-device export + import ─────────────────
  surfaces.register({
    id: 'export-bundle',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({
      passphrase: '' as string,
      includeMessages: false,
      bundleBase64: '' as string,
    }),
  });
  surfaces.register({
    id: 'import-bundle',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({
      passphrase: '' as string,
      bundleBase64: '' as string,
    }),
  });

  // ── Phase 47 (v9) — federated-search surface ─────────────────────
  surfaces.register({
    id: 'federated-search',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'session',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({
      queryText: '' as string,
      activeQueryId: null as string | null,
    }),
  });

  // ── Phase 39 (v7) — plugin marketplace + audit-anchor surface ────
  surfaces.register({
    id: 'plugin-marketplace',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({ filter: 'all' as 'all' | 'installed' | 'available' }),
  });
  surfaces.register({
    id: 'audit-anchor',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({ lastAnchoredAt: null as string | null }),
  });

  // ── Phase 31 (v6) — group call surfaces. N-party signaling lands here;
  // mesh-of-peerconnections orchestrator is the GroupCallMeshOrchestrator
  // stub in src/calls/. Real WebRTC wiring is deferred to v7.
  surfaces.register({
    id: 'group-call-incoming',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'voice',
    stateSchema: {},
    initialState: () => ({ callId: '', inviterPubkey: '', kind: 'audio' as 'audio' | 'video' }),
  });
  surfaces.register({
    id: 'group-call-active',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'session',
    environment: 'voice',
    stateSchema: {},
    initialState: () => ({
      callId: '',
      participantCount: 0,
      audioMuted: false,
      videoOn: false,
    }),
  });
  surfaces.register({
    id: 'call-roster',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'voice',
    stateSchema: {},
    initialState: () => ({ callId: '' }),
  });

  // ── Phase 27.1 — ALDS Dedicated Social Communications Surfaces (ADSCS) ──
  // Top-level persistent surface listing the user's social groups and
  // unread mention badge. Five presentation styles share this entry point;
  // a per-group `social-feed` transient renders the active group.
  surfaces.register({
    id: 'env-social',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'longterm',
    environment: 'social',
    stateSchema: {},
    initialState: () => ({
      selectedGroupId: null as string | null,
      filterKind: null as null | 'feed' | 'timeline' | 'gallery' | 'broadcast' | 'channel' | 'dm',
    }),
  });

  // Per-group post feed. Renderer picks layout from group.presentationStyle
  // or the subscriber's presentationOverride.
  surfaces.register({
    id: 'social-feed',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'session',
    environment: 'social',
    stateSchema: {},
    initialState: () => ({
      groupId: '' as string,
      composerOpen: false,
      composerText: '',
    }),
  });

  // Phase 27.4 rich compose surface — text + media manifest IDs + link card
  // + multi-option poll. Returns to social-feed on publish.
  surfaces.register({
    id: 'social-compose',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'social',
    stateSchema: {},
    initialState: () => ({
      groupId: '' as string,
      text: '',
      mediaManifestIds: '',  // comma-separated for MVP
      linkCardUrl: '',
      pollOptions: '',  // newline-separated
    }),
  });

  // Phase 27.3 broadcast renderer — Telegram-style one-way feed. Members
  // see posts; only moderators can publish top-level (replies still open).
  surfaces.register({
    id: 'social-broadcast',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'session',
    environment: 'social',
    stateSchema: {},
    initialState: () => ({ groupId: '' as string, composerOpen: false, composerText: '' }),
  });

  // Phase 27.3 gallery renderer — Instagram-style grid of media-bearing posts.
  surfaces.register({
    id: 'social-gallery',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'session',
    environment: 'social',
    stateSchema: {},
    initialState: () => ({ groupId: '' as string, openedHash: null as string | null }),
  });

  // Phase 27.3 stories renderer — ephemeral 24h TTL posts. Dedicated
  // surface so they don't pollute the main feed; auto-expires via sweeper.
  surfaces.register({
    id: 'social-stories',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'session',
    environment: 'social',
    stateSchema: {},
    initialState: () => ({ groupId: '' as string, viewingIndex: 0 }),
  });

  // Slack-style channel renderer — sidebar of groups + main message panel
  // + thread panel for selected post's replies. Reuses same data substrate
  // as `social-feed`; just a different presentation.
  surfaces.register({
    id: 'social-channel',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'session',
    environment: 'social',
    stateSchema: {},
    initialState: () => ({
      selectedGroupId: null as string | null,
      threadParentHash: null as string | null,
    }),
  });

  // Phase 32.1 (v6) — dedicated DM-in-social surface for 1:1 chats
  // backed by a kind:'dm' SocialGroupRecord.
  surfaces.register({
    id: 'social-dm',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'session',
    environment: 'social',
    stateSchema: {},
    initialState: () => ({ dmGroupId: '' as string, composerText: '' }),
  });

  // Phase 32.3 (v6) — unified contact picker that bridges env-chat +
  // env-social. Returns selected peerInboxKey via localStorage handoff
  // (same pattern as alds-selected-*).
  surfaces.register({
    id: 'contact-picker',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({
      intent: 'social-dm' as 'social-dm' | 'env-chat-thread' | 'call-target-audio' | 'call-target-video',
      query: '',
    }),
  });

  // Group creation / settings transient.
  surfaces.register({
    id: 'social-group-config',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'social',
    stateSchema: {},
    initialState: () => ({
      mode: 'create' as 'create' | 'edit',
      groupId: '' as string,
      name: '',
      kind: 'feed' as 'feed' | 'timeline' | 'gallery' | 'broadcast' | 'channel' | 'dm',
      visibility: 'private' as 'private' | 'federated' | 'public-bulletin',
      description: '',
    }),
  });

  // ── Phase 15 — bulletin pinning ─────────────────────────────────
  surfaces.register({
    id: 'bulletin-config',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({
      label: '',
      url: '',
      authKind: 'pinata' as 'pinata' | 'web3-storage' | 'ipfs-cluster' | 'public-gateway',
    }),
  });
  surfaces.register({
    id: 'bulletin-published',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'ephemeral',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({ limit: 25 }),
  });

  // ── Phase U0 (v10+) — SASS tab-bar landing surfaces ───────────────
  // Two new persistent surfaces wired specifically for the SASS bottom
  // tab bar. Each is a Signal/Telegram-shaped entry point into the
  // appropriate surface family.
  surfaces.register({
    id: 'sass-chats',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'session',
    environment: 'chat',
    stateSchema: {},
    initialState: () => ({ filter: 'all' as 'all' | 'dms' | 'threads' | 'broadcasts' }),
  });
  surfaces.register({
    id: 'sass-more',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'session',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({}),
  });

  // Phase 52 close-out — the actual VERIFY-side surface (vs `audit-anchor`
  // which is the Phase 39 publish-side). Wires `AuditVerifier.verifyAnchors`
  // + (future) `verifyAnchorByQuorum` into a user-facing panel.
  surfaces.register({
    id: 'sass-audit-verify',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'session',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({}),
  });

  // ── v10 Phase 55-57 surfaces ─────────────────────────────────────
  // Wires ModerationManager / SmsGatewayManager / BleQueueManager into
  // user-facing panels reachable from sass-more.

  // Phase 55 — pending posts list + vote affordance per draft.
  surfaces.register({
    id: 'sass-prepublish-vote',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'session',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({ groupId: null as string | null }),
  });

  // Phase 56 — BLE queue + peer book panel.
  surfaces.register({
    id: 'sass-ble-status',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'session',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({}),
  });

  // Phase 57 — SMS subscriber roster (operator-side).
  surfaces.register({
    id: 'sass-sms-subscribers',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'session',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({}),
  });

  // ── v11 Phase 59 — Phase 53.b key recovery surfaces ──────────────
  // Wires KeyRecoveryManager into a 2-step wizard reachable from
  // sass-more "Recovery" section.

  // Setup side: pick threshold + peers + send shares.
  surfaces.register({
    id: 'sass-key-recovery-setup',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'session',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({ threshold: 3 as number }),
  });

  // Restore side: list held incoming shares + reconstruct.
  surfaces.register({
    id: 'sass-key-recovery-restore',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'session',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({}),
  });

  // ── Phase 70 — Personal AI helper (on-device WebLLM) ──────────────
  // Transient chat surface summonable from the floating action button.
  // Backed by PersonalHelper singleton in src/ai/PersonalHelper.ts.
  // Model loads lazily on first ensureReady() — no cost until used.
  surfaces.register({
    id: 'personal-ai-chat',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'session',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({ draft: '' }),
  });

  // ── Phase 73 interim — surface discovery drawer ────────────────────
  // Lists every registered surface grouped by environment so users can
  // reach features that don't have an obvious entry point elsewhere in
  // the SASS shell. Read-only navigation aid; concept-driven full
  // Phase 73 still ships later.
  surfaces.register({
    id: 'sass-surface-map',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'session',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({ filter: '' }),
  });

  // ── Phase 71 partial — personal moderation mascot drawer ───────────
  // Per-category action settings + kid-mode lock + filter log review.
  // L2 / UDC integration deferred to Phase 71 full (needs Phase 67).
  surfaces.register({
    id: 'sass-mascot',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'session',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({ pin: '' }),
  });

  // ── Phase 69.A — news-paper digital newspaper surface ──────────────
  // Full feed in newspaper layout + operator publish form. Backing
  // store: news_headlines table (Dexie v28). RSS aggregator daemon
  // (Phase 69.B) will publish into this table via signed envelopes.
  surfaces.register({
    id: 'news-paper',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'session',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({ filter: 'active' as 'active' | 'all' }),
  });

  // ── Phase 73 full — shell picker drawer ────────────────────────────
  // Choose between adult / kids / teens shells. Foundation lands with
  // only adult-hub registered; subsequent ships add Kids C (Pip), Kids
  // A (Tiles), Teens C (Pocket Crew), Teens A (Vibes), Teens B (Deck).
  // See docs/alds-shells-design.md.
  surfaces.register({
    id: 'sass-shell-chooser',
    version: '0.1.0',
    surfaceClass: 'transient',
    persistence: 'session',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({}),
  });

  // Generic shell-home dispatch surface. Renders the active shell's
  // Home component (read via useShell()). Adult-hub doesn't define a
  // Home, so SurfaceStack only routes to this when the active shell
  // has shell.Home set (v11.g Pip onward).
  surfaces.register({
    id: 'shell-home',
    version: '0.1.0',
    surfaceClass: 'persistent',
    persistence: 'session',
    environment: 'hub',
    stateSchema: {},
    initialState: () => ({}),
  });
}
